import React, { useEffect, useState, useRef } from "react";
import {
  Modal as AntdModal,
  Form,
  Input,
  DatePicker,
  Radio,
  Switch,
  Button,
  Typography,
  Select,
  Spin,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import api from "../../../../services/api";
import { REFERENCE_OPTIONS } from "../constants/callConstants";
import { NotebookPen, CalendarDays } from "lucide-react";
import { notify } from "../../../../services/toast.config";

import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import { callActions } from "../../../../core/data/redux/live-call/slice";
import { getWhatsAppNumber } from "../helpers/whatsapp.helper";

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

export interface LogCallLeadModalProps {
  open: boolean;
  callData: {
    name: string;
    mobileNumber: string;
    id: any;
    form_id?: any;
    company_id?: any;
    company_type?: any;
    pipeline_char?: any;
  };
  signature: string | null;
  onSuccess: () => void;
  onRedial: () => void;
}

const LogCallLeadModal: React.FC<LogCallLeadModalProps> = ({
  open,
  callData,
  signature,
  onSuccess,
  onRedial,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const pendingNote = useSelector((s: any) => s.liveCall.pendingNote);
  const [frozenCallData, setFrozenCallData] = useState<any>(null);
  const [stages, setStages] = useState<string[]>([]);
  const [selectedStage, setSelectedStage] = useState<string>("");
  const [isLoadingStages, setIsLoadingStages] = useState(false);
  const [referenceType, setReferenceType] = useState("");
  const [expandedSection, setExpandedSection] = useState<string | null>(
    REFERENCE_OPTIONS[0]?.parent ?? null
  );
  const [noteDescription, setNoteDescription] = useState("");
  const [reminderDateTime, setReminderDateTime] = useState<Dayjs | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{
    selectedStage?: string;
    referenceType?: string;
    noteDescription?: string;
    reminderDateTime?: string;
  }>({});

  const stageRef = useRef<HTMLDivElement | null>(null);
  const outcomeRef = useRef<HTMLDivElement | null>(null);
  const noteRef = useRef<HTMLDivElement | null>(null);
  const reminderRef = useRef<HTMLDivElement | null>(null);
  const initialStageRef = useRef<string>(""); // tracks actual backend stage
  const isCallLater = referenceType === "Call Later";

  useEffect(() => {
    if (open && callData) {
      setFrozenCallData(callData);
    }
  }, [open, callData]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setExpandedSection(REFERENCE_OPTIONS[0]?.parent ?? null);
      setNoteDescription(pendingNote || "");
      if (frozenCallData?.company_id) fetchPipelineStages();
    } else {
      document.body.style.overflow = "";
      resetForm();
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, frozenCallData]);

  const resetForm = () => {
    setFrozenCallData(null);
    setSelectedStage("");
    setStages([]);
    setReferenceType("");
    setNoteDescription("");
    setReminderDateTime(null);
    setFormErrors({});
    setSubmitError(null);
    setIsSubmitting(false);
    setExpandedSection(REFERENCE_OPTIONS[0]?.parent ?? null);
    dispatch(callActions.setPendingNote(""));
  };

  const fetchPipelineStages = async () => {
    setIsLoadingStages(true);
    try {
      // 1. Fetch lead's actual current stage from backend
      let actualStage = frozenCallData?.pipeline_char || "";
      try {
        const leadRes = await api.post("leadService/getLeadById", {
          signature,
          lead_id: frozenCallData?.id,
        });
        const leadData = leadRes.data?.data?.data;
        if (leadData?.pipeline_char) {
          actualStage = leadData.pipeline_char;
        }
      } catch {
        // Fallback to prop value if fresh fetch fails
      }
      initialStageRef.current = actualStage;

      // 2. Fetch pipeline stages list
      const res = await api.post("leadService/getLeadFormsByCompany", {
        signature,
        company_id: frozenCallData?.company_id,
        company_type: frozenCallData?.company_type,
      });
      const forms = res.data.data.data || [];
      const matched = forms.find((f: any) => String(f.key) === String(frozenCallData?.form_id));
      if (matched?.pipeline_id) {
        const stagesRes = await api.post("leadService/getAllPipelinesById", {
          signature,
          id: matched.pipeline_id,
        });
        const allStages: string[] = stagesRes.data.data.data?.[0]?.Stages || [];
        setStages(allStages);
        setSelectedStage(actualStage);
      }
    } catch (e) {
      notify.error("Failed to load pipeline stages!");
    } finally {
      setIsLoadingStages(false);
    }
  };

  const validate = () => {
    const errors: typeof formErrors = {};
    if (!selectedStage) errors.selectedStage = "Please select a pipeline stage";
    if (!referenceType) errors.referenceType = "Please select a call outcome";
    if (!noteDescription.trim()) errors.noteDescription = "Notes are mandatory — please describe the call";
    if (isCallLater && !reminderDateTime)
      errors.reminderDateTime = "Reminder is required when scheduling a callback";
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      notify.error(firstError!);
      setTimeout(() => {
        if (errors.selectedStage && stageRef.current) {
          stageRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (errors.referenceType && outcomeRef.current) {
          outcomeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (errors.noteDescription && noteRef.current) {
          noteRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (errors.reminderDateTime && reminderRef.current) {
          reminderRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 50);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!frozenCallData?.id || !frozenCallData?.company_id) {
      const msg = "Call data is missing. Please close this modal and try again.";
      setSubmitError(msg);
      notify.error(msg);
      return;
    }

    if (!validate()) return;
    setSubmitError(null);
    setIsSubmitting(true);

    const leadId = frozenCallData.id || null;
    const composedNote = `${referenceType} - ${noteDescription.trim()}`;
    try {
      await api.post("leadService/addLeadnote", {
        signature,
        lead_id: leadId,
        company_id: frozenCallData?.company_id,
        company_type: frozenCallData?.company_type,
        title: "Call Note",
        note: composedNote,
      });
      // Only hit updateLeadResponsePipeline when the stage actually changed
      const stageChanged =
        selectedStage &&
        String(selectedStage).trim() !== String(initialStageRef.current).trim();

      if (stageChanged) {
        await api.post("leadService/updateLeadResponsePipeline", {
          signature,
          pipeline: selectedStage,
          lead_id: leadId,
        });
      }
      if (reminderDateTime) {
        await api.post("leadService/addLeadFollowUp", {
          signature,
          lead_id: leadId,
          company_id: frozenCallData?.company_id,
          company_type: frozenCallData?.company_type,
          follow_up_date: reminderDateTime.toISOString(),
        });
      }
      resetForm();
      onSuccess();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.data?.message?.[0]?.msg ||
        error?.message ||
        "Something went wrong. Please try again.";
      setSubmitError(msg);
      notify.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSection = (parent: string) => {
    setExpandedSection((prev) => (prev === parent ? null : parent));
  };

  const handleOptionSelect = (value: string, parentGroup: string) => {
    setReferenceType(value);
    if (formErrors.referenceType) setFormErrors((p) => ({ ...p, referenceType: undefined }));
    if (submitError) setSubmitError(null);
    if (value !== "Call Later") {
      setReminderDateTime(null);
      setFormErrors((p) => ({ ...p, reminderDateTime: undefined }));
    }
    setExpandedSection(parentGroup);
  };

  return (
    <>
      <style>{`
        .lcd-section-body {
          overflow: hidden;
          max-height: 0;
          transition: max-height 0.28s ease, padding 0.2s ease;
        }
        .lcd-section-body.open { max-height: 600px; }
        .lcd-chevron {
          transition: transform 0.25s ease;
          color: #94a3b8;
          font-size: 11px;
        }
        .lcd-chevron.open {
          transform: rotate(180deg);
          color: #1c71e2;
        }
        .lcd-option-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 14px;
          border: 1.5px solid #e8ecf1;
          border-radius: 10px;
          cursor: pointer;
          background: #fff;
          transition: border-color 0.15s, background 0.15s;
          margin-bottom: 6px;
        }
        .lcd-option-row:last-child { margin-bottom: 0; }
        .lcd-option-row:hover { border-color: #93c5fd; background: #f0f7ff; }
        .lcd-option-row.selected { border-color: #1c71e2; background: #eff6ff; }
        .lcd-radio-circle {
          width: 18px; height: 18px;
          border: 2px solid #d1d5db;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          background: #fff;
          transition: border-color 0.15s;
        }
        .lcd-option-row.selected .lcd-radio-circle { border-color: #1c71e2; }
        .lcd-radio-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #1c71e2;
          transform: scale(0);
          transition: transform 0.15s;
        }
        .lcd-option-row.selected .lcd-radio-dot { transform: scale(1); }
      `}</style>

      <AntdModal
        open={open}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        width={580}
        zIndex={9998}
        getContainer={() => document.body}
        styles={{
          content: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            maxHeight: "92vh",
            overflow: "hidden",
            borderRadius: 20,
            boxShadow: "0 25px 60px rgba(0,0,0,0.18)",
          },
          header: {
            flexShrink: 0,
            padding: "18px 22px 14px",
            borderBottom: "1px solid #f1f5f9",
            marginBottom: 0,
            borderRadius: "20px 20px 0 0",
          },
          body: {
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "16px 20px 8px",
            scrollbarWidth: "thin" as any,
            scrollbarColor: "#e2e8f0 transparent",
          },
          footer: {
            flexShrink: 0,
            padding: "12px 22px 18px",
            borderTop: "1px solid #f1f5f9",
            marginTop: 0,
          },
          mask: {
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            background: "rgba(15, 23, 42, 0.5)",
          },
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 36, height: 36,
                background: "#eff6ff",
                borderRadius: 10,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <NotebookPen size={18} color="#1D71E3" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a" }}>
                  Log Call
                </div>
                {frozenCallData && (
                  <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400, marginTop: 1 }}>
                    {frozenCallData.name} · {frozenCallData.mobileNumber}
                  </div>
                )}
              </div>
            </div>
            {frozenCallData?.mobileNumber && (
              <button
                type="button"
                className="whatsapp-icon"
                title="Open in WhatsApp"
                onClick={() => {
                  const phone = getWhatsAppNumber(frozenCallData.mobileNumber);
                  if (phone) window.open(`https://wa.me/${phone}`, "_blank");
                }}
              >
                <i className="ti ti-brand-whatsapp" />
              </button>
            )}
          </div>
        }
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button
              size="middle"
              style={{
                minWidth: 120, borderRadius: 12,
                fontWeight: 600, fontSize: 13, height: 40,
                borderColor: "#fca5a5", color: "#dc2626", background: "#fef2f2",
              }}
              disabled={isSubmitting}
              onClick={() => { resetForm(); onRedial(); }}
            >
              <i className="ti ti-phone-call" style={{ marginRight: 4 }} />Redial
            </Button>
            <Button
              type="primary"
              size="middle"
              style={{
                minWidth: 148, borderRadius: 12,
                fontWeight: 600, fontSize: 13, height: 40,
                background: "#1D71E3", border: "none",
              }}
              loading={isSubmitting}
              onClick={handleSubmit}
            >
              Save & Next Call
            </Button>
          </div>
        }
      >
        <Form layout="vertical">
          {submitError && (
            <div style={{
              fontSize: 13, color: "#b91c1c", marginBottom: 14,
              padding: "8px 12px", background: "#fef2f2",
              border: "1px solid #fecaca", borderRadius: 10,
            }}>
              {submitError}
            </div>
          )}

          <div ref={stageRef}>
            <Form.Item
              style={{ marginBottom: 16 }}
              label={
                <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", letterSpacing: 0.5, textTransform: "uppercase" }}>
                  Pipeline Stage <span style={{ color: "#ef4444" }}>*</span>
                </span>
              }
            >
              {isLoadingStages ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                  <Spin size="small" />
                  <Text style={{ fontSize: 13, color: "#9ca3af" }}>Loading stages…</Text>
                </div>
              ) : (
                <div style={{
                  border: `1.5px solid ${formErrors.selectedStage ? "#fca5a5" : "#e2e8f0"}`,
                  borderRadius: 12, overflow: "hidden",
                  transition: "border-color 0.2s",
                }}>
                  <Select
                    bordered={false}
                    value={selectedStage || undefined}
                    placeholder="Select a pipeline stage"
                    style={{ width: "100%", height: 40 }}
                    onChange={(val) => {
                      setSelectedStage(val || "");
                      if (formErrors.selectedStage)
                        setFormErrors((p) => ({ ...p, selectedStage: undefined }));
                      if (submitError) setSubmitError(null);
                    }}
                  >
                    {stages.map((stage) => (
                      <Option key={stage} value={stage}>{stage}</Option>
                    ))}
                  </Select>
                </div>
              )}
              {formErrors.selectedStage && (
                <div style={{
                  fontSize: 12, color: "#ef4444", marginTop: 6,
                  padding: "5px 10px", background: "#fff5f5",
                  border: "1px solid #fecaca", borderRadius: 8,
                }}>
                  {formErrors.selectedStage}
                </div>
              )}
            </Form.Item>
          </div>

          <div ref={outcomeRef}>
            <Form.Item style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {REFERENCE_OPTIONS.filter((item) => item.parent !== "Add To Zyvo").map((group, gi) => {
                  const isOpen = expandedSection === group.parent;
                  const hasSelected = group.children.some((c) => c.value === referenceType);
                  return (
                    <div
                      key={group.parent}
                      style={{
                        border: `1.5px solid ${hasSelected ? "#bfdbfe" : isOpen ? "#dbeafe" : "#e2e8f0"}`,
                        borderRadius: 14,
                        overflow: "hidden",
                        background: hasSelected ? "#f8fbff" : "#fff",
                        transition: "border-color 0.2s, background 0.2s",
                      }}
                    >
                      <div
                        onClick={() => toggleSection(group.parent)}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "13px 16px", cursor: "pointer", userSelect: "none",
                          borderBottom: isOpen ? "1px solid #f1f5f9" : "none",
                          background: isOpen ? "#f8fbff" : "transparent",
                          transition: "background 0.15s",
                        }}
                      >
                        <div style={{
                          width: 24, height: 24, borderRadius: "50%",
                          background: hasSelected ? "#10b981" : isOpen ? "#dbeafe" : "#f1f5f9",
                          color: hasSelected ? "#fff" : isOpen ? "#2563eb" : "#64748b",
                          fontSize: 11, fontWeight: 700,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0, transition: "background 0.2s, color 0.2s",
                        }}>
                          {hasSelected ? "✓" : gi + 1}
                        </div>

                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{
                            fontSize: 13.5, fontWeight: 600,
                            color: hasSelected ? "#1d4ed8" : "#1e293b",
                          }}>
                            {group.parent}
                          </span>
                          {group.hint && (
                            <span style={{ fontSize: 11, color: "#5F6388" }}>
                              ({group.hint})
                            </span>
                          )}
                          {hasSelected && !isOpen && (
                            <span style={{
                              fontSize: 11, fontWeight: 500,
                              padding: "2px 8px", borderRadius: 20,
                              background: "#dbeafe", color: "#2563eb",
                            }}>
                              {group.children.find((c) => c.value === referenceType)?.label}
                            </span>
                          )}
                        </div>

                        <svg
                          className={`lcd-chevron${isOpen ? " open" : ""}`}
                          width="14" height="14" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2.5"
                          strokeLinecap="round" strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </div>

                      <div className={`lcd-section-body${isOpen ? " open" : ""}`}>
                        <Radio.Group value={referenceType} style={{ width: "100%", display: "block" }}>
                          <div style={{ padding: "10px 12px 12px" }}>
                            {group.children.map((opt) => {
                              const isSelected = referenceType === opt.value;
                              return (
                                <div
                                  key={opt.value}
                                  className={`lcd-option-row${isSelected ? " selected" : ""}`}
                                  onClick={() => handleOptionSelect(opt.value, group.parent)}
                                >
                                  <div className="lcd-radio-circle">
                                    <div className="lcd-radio-dot" />
                                  </div>
                                  <span style={{
                                    fontSize: 13.5,
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isSelected ? "#1D71E3" : "#334155",
                                  }}>
                                    {opt.label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </Radio.Group>
                      </div>
                    </div>
                  );
                })}
              </div>
              {formErrors.referenceType && (
                <div style={{
                  fontSize: 12, color: "#ef4444", marginTop: 6,
                  padding: "5px 10px", background: "#fff5f5",
                  border: "1px solid #fecaca", borderRadius: 8,
                }}>
                  {formErrors.referenceType}
                </div>
              )}
            </Form.Item>
          </div>



          <div
            ref={reminderRef}
            style={{
              background: "#f8fafc",
              padding: "16px",
              borderRadius: "16px",
              border: `1.5px solid ${formErrors.reminderDateTime ? "#fecaca" : "#e5e7eb"}`,
              marginBottom: 16,
              transition: "border-color 0.2s, background 0.2s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #eef2f6"
                }}>
                  <CalendarDays size={16} color="#1D71E3" />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", lineHeight: 1.2 }}>
                    Follow-Up Reminder
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                    Schedule a callback for this lead
                  </div>
                </div>
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                background: isCallLater ? "#fee2e2" : "#f1f5f9",
                color: isCallLater ? "#dc2626" : "#64748b",
                textTransform: "uppercase", letterSpacing: 0.5,
              }}>
                {isCallLater ? "Required" : "Optional"}
              </span>
            </div>

            <DatePicker
              showTime={{ format: "hh:mm A", use12Hours: true }}
              format="DD MMM YYYY, hh:mm A"
              style={{
                width: "100%", borderRadius: 10, height: 42,
                borderColor: formErrors.reminderDateTime ? "#fca5a5" : "#e2e8f0",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
              }}
              placeholder="Select follow-up date & time"
              value={reminderDateTime}
              onChange={(date) => {
                setReminderDateTime(date);
                if (formErrors.reminderDateTime)
                  setFormErrors((p) => ({ ...p, reminderDateTime: undefined }));
              }}
              disabledDate={(current) => current && current < dayjs().startOf("day")}
            />
            {formErrors.reminderDateTime && (
              <div style={{
                fontSize: 12, color: "#ef4444", marginTop: 8,
                padding: "6px 12px", background: "#fff5f5",
                border: "1px solid #fecaca", borderRadius: 8,
              }}>
                {formErrors.reminderDateTime}
              </div>
            )}
          </div>

          <div ref={noteRef}>
            <Form.Item
              style={{ marginBottom: 16 }}
              label={
                <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", letterSpacing: 0.5, textTransform: "uppercase" }}>
                  Notes <span style={{ color: "#ef4444" }}>*</span>
                </span>
              }
            >
              <div style={{
                border: `1.5px solid ${formErrors.noteDescription ? "#fca5a5" : "#e2e8f0"}`,
                borderRadius: 12, overflow: "hidden",
                transition: "border-color 0.2s",
              }}>
                <TextArea
                  bordered={false}
                  rows={3}
                  placeholder={
                    referenceType.includes("Others")
                      ? "Explain the reason clearly..."
                      : "Describe the call outcome, student's response, next steps..."
                  }
                  value={noteDescription}
                  style={{ borderRadius: 0, fontSize: 13, resize: "none", padding: "10px 13px" }}
                  onChange={(e) => {
                    setNoteDescription(e.target.value);
                    if (formErrors.noteDescription)
                      setFormErrors((p) => ({ ...p, noteDescription: undefined }));
                    if (submitError) setSubmitError(null);
                  }}
                />
              </div>
              {formErrors.noteDescription && (
                <div style={{
                  fontSize: 12, color: "#ef4444", marginTop: 6,
                  padding: "5px 10px", background: "#fff5f5",
                  border: "1px solid #fecaca", borderRadius: 8,
                }}>
                  {formErrors.noteDescription}
                </div>
              )}
            </Form.Item>
          </div>
        </Form>
      </AntdModal>
    </>
  );
};

export default LogCallLeadModal;
